import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, ShieldCheck, RotateCcw, Cpu, Heart, Leaf, Sparkles } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";

const HERO_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/981795dc-3f85-41a2-8218-f3db7d35fb62/images/1eda49e1163944207d6aeeeeedf8dfd5a5c9ccae2cf98ac9b0e8b221e445378b.png";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const { t } = useTranslation();

  useEffect(() => {
    api
      .get("/products", { params: { featured: true } })
      .then((r) => setFeatured(r.data));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO — full-bleed image with overlay */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Bureau Nord Stella Tech"
            className="w-full h-full object-cover opacity-90"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#001F3F] via-[#001F3F]/85 to-[#001F3F]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#001F3F] via-transparent to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-24 pb-32 lg:pt-32 lg:pb-40">
          <div className="max-w-3xl fade-up">
            <div className="text-xs font-bold uppercase tracking-[0.4em] text-[#C5CDD8]">
              {t("home.hero_eyebrow")}
            </div>
            <h1 className="font-display mt-8 text-5xl sm:text-7xl lg:text-8xl font-bold uppercase tracking-tighter leading-[0.85] text-white">
              <Trans
                i18nKey="home.hero_title"
                components={{ br: <br />, em: <span className="silver-shimmer" /> }}
              />
            </h1>
            <p className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-white/75">
              {t("home.hero_subtitle")}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 border border-white bg-white text-[#001F3F] hover:bg-transparent hover:text-white transition-all uppercase tracking-[0.25em] text-xs font-bold px-8 py-4"
                data-testid="hero-shop-cta"
              >
                {t("home.shop_cta")} <ArrowRight size={14} />
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 border border-white/40 bg-transparent text-white hover:bg-white hover:text-[#001F3F] transition-all uppercase tracking-[0.25em] text-xs font-bold px-8 py-4"
              >
                {t("home.story_cta")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-b border-white/10 py-5 bg-[#000B1A] text-white/80 overflow-hidden">
        <div className="marquee">
          <div className="marquee-track">
            {Array.from({ length: 2 }).map((_, j) => (
              <div key={j} className="flex items-center gap-12 px-6 text-xs font-bold uppercase tracking-[0.35em]">
                <span>Livraison offerte dès 75 €</span>
                <span className="text-[#C5CDD8]">●</span>
                <span>Retours sous 14 jours</span>
                <span className="text-[#C5CDD8]">●</span>
                <span>Paiement sécurisé Stripe</span>
                <span className="text-[#C5CDD8]">●</span>
                <span>Livraison 4 à 7 jours ouvrés</span>
                <span className="text-[#C5CDD8]">●</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 stagger">
          {[
            { i: Heart, t: "Santé d'abord", d: "Validé sur des critères ergonomiques mesurables." },
            { i: Cpu, t: "Tech invisible", d: "L'innovation disparaît au profit de l'usage." },
            { i: Leaf, t: "Matières nobles", d: "Bambou, noyer, aluminium recyclé, biosourcés." },
            { i: Sparkles, t: "Design éditorial", d: "Le bel objet ne distrait pas. Il éveille." },
          ].map((p, i) => (
            <div key={i} className="border-t border-white/10 pt-6">
              <p.i size={26} strokeWidth={1.25} className="text-[#C5CDD8]" />
              <h3 className="font-display mt-4 text-xl font-bold tracking-tight text-white">{p.t}</h3>
              <p className="mt-3 text-sm text-white/60 leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <div className="flex items-end justify-between mb-12 border-t border-white/10 pt-12">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
              Nos univers
            </div>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-white">
              Connecté. Confortable. Calme.
            </h2>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <Link
            to="/shop?category=smart"
            className="group relative aspect-[16/10] overflow-hidden bg-[#0A2A4F] border border-white/10"
            data-testid="cat-smart"
          >
            <img
              src="https://images.unsplash.com/photo-1507473885765-e6ed057f782c?auto=format&fit=crop&w=1200&q=80"
              alt="Smart Tech"
              className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#001F3F] via-[#001F3F]/40 to-transparent" />
            <div className="absolute bottom-0 left-0 p-8 text-white">
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">01</div>
              <div className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tighter mt-2">Smart Tech</div>
            </div>
          </Link>
          <Link
            to="/shop?category=wellness"
            className="group relative aspect-[16/10] overflow-hidden bg-[#0A2A4F] border border-white/10"
            data-testid="cat-wellness"
          >
            <img
              src="https://images.unsplash.com/photo-1611532736597-de2d4265fba3?auto=format&fit=crop&w=1200&q=80"
              alt="Home Office Wellness"
              className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#001F3F] via-[#001F3F]/40 to-transparent" />
            <div className="absolute bottom-0 left-0 p-8 text-white">
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">02</div>
              <div className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tighter mt-2">Home Office Wellness</div>
            </div>
          </Link>
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <div className="flex items-end justify-between mb-12 border-t border-white/10 pt-12">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
              Sélection
            </div>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-white">
              Pièces en vedette
            </h2>
          </div>
          <Link
            to="/shop"
            className="hidden sm:inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.3em] text-white/70 hover:text-white transition"
            data-testid="featured-view-all"
          >
            Tout voir <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12 stagger">
          {featured.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="border-t border-white/10 bg-[#0A2A4F]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid md:grid-cols-3 gap-8">
          {[
            { icon: Truck, t: "Livraison Mondiale", d: "Expédition en 5-10 jours ouvrés depuis nos ateliers partenaires." },
            { icon: ShieldCheck, t: "Paiement Sécurisé", d: "Cryptage SSL et paiements certifiés Stripe PCI-DSS." },
            { icon: RotateCcw, t: "Rétractation 14 jours", d: "Pas convaincu ? Renvoyez selon les CGV." },
          ].map((v, i) => (
            <div key={i} className="flex items-start gap-4">
              <v.icon size={28} strokeWidth={1.25} className="text-[#C5CDD8]" />
              <div>
                <div className="font-display text-lg font-bold tracking-tight text-white">{v.t}</div>
                <div className="text-sm text-white/60 mt-1">{v.d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
